import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Typography,
  CircularProgress,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  TablePagination,
} from '@material-ui/core';
import toast from 'react-hot-toast';
import { makeStyles } from '@material-ui/core/styles';
import { Description, Favorite, Comment, Refresh, TrendingUp, Search, Clear, InsertPhotoOutlined } from '@material-ui/icons';
import { POST_SORT_COLUMNS, sortPosts } from '../lib/postSorting';
import {
  getHandlerData,
  getYtHandlerData,
  creatorTypesWithNormalTypeFetch,
  creatorTypesYoutubeWithNormalTypeFetch,
  updateProfileData,
  updatePostData,
} from 'api_handlers/competitor';
import { getCurrentUser } from 'api_handlers/auth';
import {
  formatNumber,
  formatPercentage,
  formatDate,
} from '../lib/utils';
import { DashboardMetricCard } from './DashboardMetricCard';

// Category ID to Name mapping
const CATEGORY_MAP = {
  '9241': 'Aviation',
  '4': 'Beauty',
  '6': 'Sports',
  '6806': 'Comedy',
  '9076': 'Entrepreneur',
  '9274': 'Vlogging',
  '9275': 'Comedy/Entertainment',
  '11': 'Dance',
  '9276': 'Community',
  '14': 'Actor',
  '16': 'Automobile',
  '9277': 'Food/Cooking',
  '18': 'Fashion',
  '20': 'Fitness',
  '31': 'Finance',
  '9142': 'Pet Page',
  '9175': 'Daily Vlogger',
  '76': 'Parenting',
  '382': 'Motivation',
  '9208': 'Other',
  '9278': 'Tech/Gadgets',
  '9279': 'Business/Entrepreneurship',
  '9280': 'Automotive',
  '9281': 'Couple',
  '9282': 'Health & Wellness',
  '9283': 'Documentary',
  '9307': 'Art',
  '9308': 'News',
  '9309': 'Doctor',
  '9340': 'Advertising',
  '9373': 'Poetry',
  '9374': 'Anime',
  '9375': 'Podcasting',
  '9406': 'ASMR',
  '9439': 'Shopping',
  '9472': 'Law',
  '9505': 'Celebrity',
  '9538': 'Kids Learning',
  '9571': 'Celebrity/Fan',
  '9604': 'Unboxing',
  '9637': 'Home Improvement',
  '9670': 'Acting',
  '9703': 'Science',
  '9736': 'Voiceover',
  '9769': 'Jewelry',
  '9802': 'Grooming',
  '9835': 'Interior Design',
  '9868': 'Real Estate',
  '9901': 'Editing',
  '9902': 'Cinema',
  '9934': 'Spiritual',
  '9967': 'Pet Care',
  '9044': 'Career Coach',
  '9045': 'Fan Page',
  '9046': 'Gadget Review',
  '9047': 'Legal',
  '9048': 'Model',
  '9049': 'Nutrition',
  '9050': 'Product Review',
  '9051': 'Self Help And Motivation',
  '9052': 'Spirituality',
  '9053': 'Stock Market',
  '9054': 'Website And Tools',
  '9112': 'Music',
  '9113': 'Self Help',
  '9114': 'Haircare',
  '9284': 'Mystery',
  '9285': 'Family',
  '9286': 'Kids/Animation',
  '9287': 'Aerospace',
  '9407': 'Podcast',
  '9572': 'Film',
  '9803': 'Architecture',
  '9968': 'Shopping & Retail',
  '8913': 'Education',
  '8916': 'home decor',
  '4897': 'Decor',
  '9288': 'Educational',
  '9289': 'Transport',
  '9573': 'Wildlife',
  '8561': 'Sustainable',
  '8652': 'Fashion Male',
  '8718': 'Pets',
  '8945': 'Skincare',
  '5554': 'Crypto',
  '7620': 'Motivational',
  '8977': 'Agriculture',
  '8978': 'Art And Craft',
  '8979': 'Business mapping',
  '8980': 'Digital Marketing',
  '8981': 'Entertainment',
  '8982': 'Entertainment Page',
  '8983': 'Fan Page',
  '8985': 'Food',
  '8986': 'Gadget revie',
  '8987': 'Gaming',
  '8988': 'Gardening',
  '8989': 'Graphic Designer',
  '8991': 'Health',
  '8992': 'Infotainment',
  '8993': 'Investment',
  '8994': 'Kids',
  '8995': 'Legal',
  '8996': 'Lifestyle',
  '8997': 'Literature',
  '8998': 'Meme Page',
  '8999': 'Mens Grooming',
  '9000': 'Motor Vloggers',
  '9001': 'Movie Or Tv Reviews',
  '9003': 'News And Media',
  '9004': 'Photography',
  '9006': 'Skincare',
  '9007': 'Sustainability',
  '9008': 'Technology',
  '9009': 'Theatre',
  '9010': 'Travel',
  '9011': 'Website & Tool',
  '9012': 'Yoga',
};

const cleanCategoryName = (category) => {
  if (!category) return 'General';
  
  const categoryStr = category.toString().trim();
  
  // If it's just a number (ID only), look it up in the mapping
  if (/^\d+$/.test(categoryStr)) {
    return CATEGORY_MAP[categoryStr] || 'General';
  }
  
  // Handle formats like "9241 Aviation", "4 Beauty", "6806\tComedy" (with tab)
  // First, try to extract the ID and look it up
  const idMatch = categoryStr.match(/^(\d+)/);
  if (idMatch) {
    const id = idMatch[1];
    const mappedName = CATEGORY_MAP[id];
    if (mappedName) {
      return mappedName;
    }
  }
  
  // Remove leading numbers followed by space or tab to extract just the name
  let cleaned = categoryStr.replace(/^\d+[\s\t]+/, '').trim();
  
  // If it contains a tab, split and take the name part (after the ID)
  if (categoryStr.includes('\t')) {
    const parts = categoryStr.split('\t');
    cleaned = parts[parts.length - 1].trim();
  }
  
  // If it contains multiple spaces, try to extract the name part (after the number)
  if (!cleaned && /\d+\s+/.test(categoryStr)) {
    cleaned = categoryStr.replace(/^\d+\s+/, '').trim();
  }
  
  // If after cleaning it's empty or just numbers (ID only), try mapping again
  if (!cleaned || /^\d+$/.test(cleaned)) {
    return CATEGORY_MAP[cleaned] || 'General';
  }
  
  return cleaned;
};

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    marginTop: theme.spacing(0),
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[8],
    },
    textAlign: 'center',
  },
  tableHeader: {
    backgroundColor: theme.palette.background.default,
  },
  // Keep the sort arrow visible (faded) on every sortable header so users know
  // the columns can be sorted, and full-strength on the active column.
  sortLabel: {
    '& .MuiTableSortLabel-icon': { opacity: 0.4, transition: 'opacity 0.2s' },
    '&:hover .MuiTableSortLabel-icon': { opacity: 0.7 },
    '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { opacity: 1, color: theme.palette.primary.main },
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  paginationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  emptyContainer: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  thumbnail: {
    width: 40,
    height: 40,
    borderRadius: 4,
    objectFit: 'cover',
  },
}));

const getInfluencerType = (followers) => {
  if (followers >= 200 && followers <= 2000) {
    return 'Normal';
  } else if (followers >= 2000 && followers <= 9999) {
    return 'Nano';
  } else if (followers >= 10000 && followers <= 99999) {
    return 'Micro';
  } else if (followers >= 100000 && followers <= 999999) {
    return 'Macro';
  } else if (followers >= 1000000) {
    return 'Mega';
  }
  return 'Normal';
};

const calculateEngagementRate = (likes, comments, shares, views) => {
  likes = likes !== null ? likes : 0;
  comments = comments !== null ? comments : 0;
  shares = shares !== null ? shares : 0;
  if (views === 0 || views === null) {
    return 0;
  }
  const engagementRate = ((likes + comments + shares) / views) * 100;
  return parseFloat(engagementRate.toFixed(2));
};

const getBrandColor = (brandName, index) => {
  const colorMap = {
    0: '#f9a620',
    1: '#ffd449',
    2: '#aab03c',
    3: '#548c2f',
    4: '#000000',
  };
  return colorMap[index] || colorMap[0];
};

// Post/creator images are content-addressed, immutable S3 URLs. A per-render
// Date.now() cache-buster changed the <img> src on every re-render (filters/sort/
// pagination), aborting the in-flight load and blanking the image. Return the URL
// unchanged so the browser can cache it and the src stays stable.
const addCacheBuster = (url) => url || '';

export default function PostsTab({
  selectedBrand,
  competitorBrands,
  start_date,
  end_date,
  platform = 'instagram',
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const [aggregatedMetrics, setAggregatedMetrics] = useState({
    totalPosts: 0,
    totalLikes: 0,
    totalViews: 0,
    totalComments: 0,
    totalShares: 0,
  });
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [updatingProfiles, setUpdatingProfiles] = useState({}); // Track loading state per post profile
  const [updatingPosts, setUpdatingPosts] = useState({}); // Track loading state per post
  const [isStaff, setIsStaff] = useState(false);
  // Track post thumbnails that failed to load, so we swap to the placeholder cleanly.
  const [failedThumbs, setFailedThumbs] = useState({});

  // Filters for the All Posts table (client-side, over the full loaded set)
  const [searchQuery, setSearchQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [formatFilter, setFormatFilter] = useState('all');
  const [minEngagement, setMinEngagement] = useState('');
  const [minViews, setMinViews] = useState('');

  // Client-side pagination over the filtered set
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sorting for the posts table
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  };
  
  // Get is_staff status from API
  useEffect(() => {
    getCurrentUser().then((data) => setIsStaff(data.data.is_staff));
  }, []);
  
  // Handle update profile
  const handleUpdateProfile = useCallback(async (post) => {
    const profileId = post.creator_details?.id || post.creator_details?.handle || post.handle || post.channel_id;
    setUpdatingProfiles(prev => ({ ...prev, [profileId]: true }));
    
    try {
      const selectedBrandObj = competitorBrands.find((b) => b.title === selectedBrand);
      const response = await updateProfileData({
        handle: post.creator_details?.id || post.creator_details?.handle || post.handle || post.channel_id,
        brand_id: selectedBrandObj?.id || selectedBrandObj?.brand_id,
        profile_type: post.channel_id || post.video_id ? 'youtube' : 'instagram',
      });
      
      if (response && (response.status === 200 || response.status === 201)) {
        toast.success('Profile updated successfully');
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile. Please try again.');
    } finally {
      setUpdatingProfiles(prev => ({ ...prev, [profileId]: false }));
    }
  }, [selectedBrand, competitorBrands]);
  
  // Handle update post
  const handleUpdatePost = useCallback(async (post) => {
    const postId = post.id || post.shortcode || post.video_id;
    setUpdatingPosts(prev => ({ ...prev, [postId]: true }));
    
    try {
      const isYoutube = post.channel_id || post.video_id;
      const response = await updatePostData({
        post_id: post.id || post.shortcode || post.video_id,
        post_type: isYoutube ? 'youtube' : 'instagram',
      });
      
      if (response && (response.status === 200 || response.status === 201)) {
        toast.success('Post updated successfully');
      } else {
        toast.error('Failed to update post');
      }
    } catch (error) {
      console.error('Error updating post:', error);
      toast.error('Error updating post. Please try again.');
    } finally {
      setUpdatingPosts(prev => ({ ...prev, [postId]: false }));
    }
  }, []);

  // Clear data immediately when brand changes
  useEffect(() => {
    if (selectedBrand) {
      setPosts([]);
      setPagination({ count: 0, next: null, previous: null });
      setAggregatedMetrics({
        totalPosts: 0,
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
        totalViews: 0,
      });
      setSearchQuery('');
      setGenreFilter('all');
      setCategoryFilter('all');
      setFormatFilter('all');
      setMinEngagement('');
      setMinViews('');
      setPage(0);
    }
  }, [selectedBrand]);

  // Fetch aggregated metrics from API
  const fetchAggregatedMetrics = useCallback(async () => {
    if (!selectedBrand) {
      setAggregatedMetrics({
        totalPosts: 0,
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
        totalViews: 0,
      });
      return;
    }

    // Get fresh brandData from competitorBrands instead of relying on state
    const currentBrandData = competitorBrands.find((b) => b.title === selectedBrand);
    if (!currentBrandData) {
      setAggregatedMetrics({
        totalPosts: 0,
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
        totalViews: 0,
      });
      return;
    }

    setLoadingMetrics(true);
    try {
      const isYouTube = platform === 'yt' || platform === 'youtube';
      let data = null;

      // Fetch data for the selected platform only
      if (isYouTube) {
        // Fetch YouTube aggregated data only
        const ytHandle = currentBrandData.yt_handle || '';
        if (ytHandle && ytHandle.trim() !== '' && ytHandle.toLowerCase() !== 'test') {
          try {
            data = await creatorTypesYoutubeWithNormalTypeFetch(
              ytHandle,
              start_date || '',
              end_date || '',
              '',
              '',
              currentBrandData.excluded_channel_ids
            );
          } catch (error) {
            console.error('Error fetching YouTube aggregated data:', error);
          }
        }
      } else {
        // Fetch Instagram aggregated data only
        const igHandle = currentBrandData.ig_handle || '';
        if (igHandle && igHandle.trim() !== '') {
          try {
            data = await creatorTypesWithNormalTypeFetch(
              igHandle,
              start_date || '',
              end_date || '',
              '',
              '',
              currentBrandData.excluded_handles
            );
          } catch (error) {
            console.error('Error fetching Instagram aggregated data:', error);
          }
        }
      }

      // Calculate metrics from the selected platform
      let totalPosts = 0;
      let totalLikes = 0;
      let totalComments = 0;
      let totalShares = 0;
      let totalViews = 0;
      if (data && data.all_creators) {
        totalPosts = data.all_creators.count || 0;
        totalLikes = data.all_creators.likes_count || 0;
        totalComments = data.all_creators.comments_count || 0;
        totalShares = data.all_creators.shares_count || 0;
        totalViews = data.all_creators.views_count || data.all_creators.view_count || 0;
      }

      setAggregatedMetrics({
        totalPosts,
        totalLikes,
        totalComments,
        totalShares,
        totalViews,
      });
    } catch (error) {
      console.error('Error fetching aggregated metrics:', error);
      setAggregatedMetrics({
        totalPosts: 0,
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
        totalViews: 0,
      });
    } finally {
      setLoadingMetrics(false);
    }
  }, [selectedBrand, competitorBrands, start_date, end_date, platform]);

  useEffect(() => {
    if (selectedBrand && competitorBrands.length > 0) {
      const brand = competitorBrands.find((b) => b.title === selectedBrand);
      if (brand) {
        fetchAggregatedMetrics();
      }
    }
  }, [selectedBrand, competitorBrands, start_date, end_date, fetchAggregatedMetrics]);

  // Fetch ALL posts across every page so filters apply to the full set.
  const fetchPosts = useCallback(
    async () => {
      if (!selectedBrand) return;

      // Get fresh brandData from competitorBrands instead of relying on state
      const currentBrandData = competitorBrands.find((b) => b.title === selectedBrand);
      if (!currentBrandData) return;

      setLoading(true);
      try {
        const isYouTube = platform === 'yt' || platform === 'youtube';
        const ytHandle = currentBrandData.yt_handle || '';
        const igHandle = currentBrandData.ig_handle || '';
        const validYt = ytHandle && ytHandle.trim() !== '' && ytHandle.toLowerCase() !== 'test';
        const validIg = igHandle && igHandle.trim() !== '';

        // Fetch a single page of raw post results for the active platform
        const fetchPage = (pageNum) => {
          if (isYouTube) {
            if (!validYt) return Promise.resolve(null);
            return getYtHandlerData(ytHandle, '', pageNum, start_date, end_date, '', false, '');
          }
          if (!validIg) return Promise.resolve(null);
          return getHandlerData(
            igHandle, '', pageNum, '', false, '', start_date, end_date,
            currentBrandData.excluded_handles
          );
        };

        const platformLabel = isYouTube ? 'YouTube' : 'Instagram';
        const tag = (results) =>
          (results || []).map((r) => {
            r.platform = platformLabel;
            return r;
          });

        const firstResponse = await fetchPage(1);
        if (!firstResponse) {
          setPosts([]);
          setPagination({ count: 0, next: null, previous: null });
          return;
        }

        let rawResults = [...tag(firstResponse.results)];
        const total = firstResponse.count || rawResults.length;
        const pageSize = (firstResponse.results || []).length || 1;
        const MAX_PAGES = 100; // safety cap to avoid runaway loops
        const totalPages = Math.min(
          pageSize > 0 ? Math.ceil(total / pageSize) : 1,
          MAX_PAGES
        );

        // Fetch remaining pages in parallel
        if (totalPages > 1) {
          const pagePromises = [];
          for (let p = 2; p <= totalPages; p += 1) pagePromises.push(fetchPage(p));
          const responses = await Promise.all(pagePromises);
          responses.forEach((r) => {
            if (r && Array.isArray(r.results)) rawResults.push(...tag(r.results));
          });
        }

        // Add brand info to each post
        const postsWithBrand = rawResults.map((post) => ({
          ...post,
          brand: selectedBrand,
        }));

        setPosts(postsWithBrand);
        setPagination({ count: postsWithBrand.length, next: null, previous: null });
      } catch (error) {
        console.error('Error fetching posts:', error);
        setPosts([]);
        setPagination({ count: 0, next: null, previous: null });
      } finally {
        setLoading(false);
      }
    },
    [selectedBrand, competitorBrands, start_date, end_date, platform]
  );

  useEffect(() => {
    if (selectedBrand && competitorBrands.length > 0) {
      const brand = competitorBrands.find((b) => b.title === selectedBrand);
      if (brand) {
        fetchPosts();
      }
    }
  }, [selectedBrand, competitorBrands, start_date, end_date, fetchPosts]);

  const handleRefresh = () => {
    fetchPosts();
    fetchAggregatedMetrics();
    toast.success('Refreshed');
  };

  // Calculate average engagement rate from aggregated metrics
  const avgEngagement = useMemo(() => {
    if (aggregatedMetrics.totalViews === 0 || aggregatedMetrics.totalViews === null) {
      return 0;
    }
    const totalEngagement = aggregatedMetrics.totalLikes + aggregatedMetrics.totalComments + aggregatedMetrics.totalShares;
    const engagementRate = (totalEngagement / aggregatedMetrics.totalViews) * 100;
    return parseFloat(engagementRate.toFixed(2));
  }, [aggregatedMetrics]);

  // Use aggregated metrics from API instead of calculating from posts
  const metrics = {
    ...aggregatedMetrics,
    avgEngagement,
  };

  // Per-post engagement rate (mirrors the table cell logic)
  const postEngagement = (post) =>
    post.engagement_rate !== undefined && post.engagement_rate !== null && post.engagement_rate !== 0
      ? post.engagement_rate
      : calculateEngagementRate(
          post.likes_count || post.like_count || 0,
          post.comments_count || post.comment_count || 0,
          post.shares_count || post.share_count || 0,
          post.creator_details?.followers || post.view_count || post.reach || 1
        );

  // Distinct filter options derived from the currently loaded posts
  const filterOptions = useMemo(() => {
    const genres = new Set();
    const categories = new Set();
    const formats = new Set();
    posts.forEach((post) => {
      genres.add(cleanCategoryName(post.creator_details?.category || post.category));
      categories.add(getInfluencerType(post.creator_details?.followers));
      formats.add(post.post_type || post.media_type || 'Post');
    });
    return {
      genres: Array.from(genres).sort(),
      categories: Array.from(categories).sort(),
      formats: Array.from(formats).sort(),
    };
  }, [posts]);

  const hasActiveFilters =
    searchQuery !== '' ||
    genreFilter !== 'all' ||
    categoryFilter !== 'all' ||
    formatFilter !== 'all' ||
    minEngagement !== '' ||
    minViews !== '';

  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setGenreFilter('all');
    setCategoryFilter('all');
    setFormatFilter('all');
    setMinEngagement('');
    setMinViews('');
    setPage(0);
  }, []);

  // Apply all filters to the full loaded set of posts
  const filteredPosts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const minEngagementNum = minEngagement === '' ? null : Number(minEngagement);
    const minViewsNum = minViews === '' ? null : Number(minViews);

    return posts.filter((post) => {
      const name = (
        post.creator_details?.name ||
        post.creator_name ||
        post.creator ||
        post.handle ||
        post.channel_name ||
        ''
      ).toLowerCase();
      if (query && !name.includes(query)) return false;

      const genre = cleanCategoryName(post.creator_details?.category || post.category);
      if (genreFilter !== 'all' && genre !== genreFilter) return false;

      const category = getInfluencerType(post.creator_details?.followers);
      if (categoryFilter !== 'all' && category !== categoryFilter) return false;

      const format = post.post_type || post.media_type || 'Post';
      if (formatFilter !== 'all' && format !== formatFilter) return false;

      if (minEngagementNum !== null && postEngagement(post) < minEngagementNum) return false;

      const views = post.view_count || post.views || post.reach || 0;
      if (minViewsNum !== null && views < minViewsNum) return false;

      return true;
    });
  }, [posts, searchQuery, genreFilter, categoryFilter, formatFilter, minEngagement, minViews]);

  // Reset to first page whenever the filtered result set changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, genreFilter, categoryFilter, formatFilter, minEngagement, minViews]);

  // Current page slice of the filtered posts
  // Sorted view of the filtered posts (before pagination), so sorting applies
  // across the whole result set rather than just the visible page.
  const sortedPosts = useMemo(
    () =>
      sortPosts(filteredPosts, sortField, sortDir, {
        getInfluencerType,
        cleanCategoryName,
        calculateEngagementRate,
      }),
    [filteredPosts, sortField, sortDir]
  );

  const paginatedPosts = useMemo(
    () => sortedPosts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedPosts, page, rowsPerPage]
  );

  if (!selectedBrand) {
    return (
      <Paper className={classes.tableContainer}>
        <Box className={classes.emptyContainer}>
          <Typography variant="h6" color="textSecondary">
            Please select a brand to view posts
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (loading && posts.length === 0) {
    return (
      <Paper className={classes.tableContainer}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Box style={{ marginTop: 24 }}>
      {/* Metric Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={Description}
            title="Total Posts"
            value={metrics.totalPosts.toString()}
            subtitle="This period"
            trend="+12.5%"
            iconColor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={Description}
            title="Total Views"
            value={formatNumber(metrics.totalViews)}
            subtitle="All posts"
            iconColor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={Favorite}
            title="Total Likes"
            value={formatNumber(metrics.totalLikes)}
            subtitle="All posts"
            iconColor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={Comment}
            title="Total Comments"
            value={formatNumber(metrics.totalComments)}
            subtitle="All posts"
            iconColor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={TrendingUp}
            title="Avg Engagement"
            value={formatPercentage(metrics.avgEngagement)}
            subtitle="Across all posts"
            trend="+2.1%"
            iconColor="#fef3c7"
          />
        </Grid>
      </Grid>

      {/* Posts Table */}
      <Card className={classes.tableContainer}>
        <CardHeader
          title={
            <Typography variant="h6" style={{ fontWeight: 600, fontSize: '16px' }}>
              All Posts
            </Typography>
          }
          subheader={
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 0 }}>
              Detailed performance metrics for all posts
            </Typography>
          }
        />
        <CardContent style={{ padding: 0 }}>
          {/* Filter toolbar */}
          <Box
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'center',
              padding: 16,
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search creator"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ fontSize: 18, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              style={{ minWidth: 200 }}
            />
            <TextField
              select
              size="small"
              variant="outlined"
              label="Genre"
              value={genreFilter}
              onChange={(e) => setGenreFilter(e.target.value)}
              style={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Genres</MenuItem>
              {filterOptions.genres.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ minWidth: 140 }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {filterOptions.categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label="Format"
              value={formatFilter}
              onChange={(e) => setFormatFilter(e.target.value)}
              style={{ minWidth: 130 }}
            >
              <MenuItem value="all">All Formats</MenuItem>
              {filterOptions.formats.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              variant="outlined"
              type="number"
              label="Min eng. %"
              value={minEngagement}
              onChange={(e) => setMinEngagement(e.target.value)}
              style={{ width: 120 }}
            />
            <TextField
              size="small"
              variant="outlined"
              type="number"
              label="Min views"
              value={minViews}
              onChange={(e) => setMinViews(e.target.value)}
              style={{ width: 120 }}
            />
            {hasActiveFilters && (
              <Button
                size="small"
                onClick={resetFilters}
                startIcon={<Clear />}
                style={{ textTransform: 'none', color: '#6b7280' }}
              >
                Clear
              </Button>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginLeft: 'auto' }}
            >
              Showing {filteredPosts.length} of {posts.length} posts
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell align="center" style={{ fontWeight: 600 }}>Post</TableCell>
                  {POST_SORT_COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      align="center"
                      style={{ fontWeight: 600 }}
                      sortDirection={sortField === col.key ? sortDir : false}
                    >
                      <TableSortLabel
                        className={classes.sortLabel}
                        active={sortField === col.key}
                        direction={sortField === col.key ? sortDir : 'asc'}
                        hideSortIcon={false}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  {/* <TableCell align="center" style={{ fontWeight: 600 }}>Shares</TableCell> */}
                  {isStaff && platform === 'instagram' && (
                    <TableCell align="center" style={{ fontWeight: 600 }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
          <TableBody>
            {loading && posts.length > 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isStaff && platform === 'instagram' ? 13 : 12}
                  style={{ textAlign: 'center', padding: 40 }}
                >
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredPosts.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isStaff && platform === 'instagram' ? 13 : 12}
                  style={{ textAlign: 'center', padding: 40 }}
                >
                  <Typography variant="body1" color="textSecondary">
                    {posts.length === 0
                      ? `No posts found for ${selectedBrand}`
                      : 'No posts match the selected filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPosts.map((post, index) => {
                // Handle YouTube API response structure
                const engagementRate = post.engagement_rate !== undefined && post.engagement_rate !== null && post.engagement_rate !== 0
                  ? post.engagement_rate
                  : calculateEngagementRate(
                      post.likes_count || post.like_count || 0,
                      post.comments_count || post.comment_count || 0,
                      post.shares_count || post.share_count || 0,
                      post.creator_details?.followers || post.view_count || post.reach || 1
                    );
                const postDate =
                  post.timestamp || 
                  post.created_time || 
                  post.published_at ||
                  post.post_creation_date;

                return (
                  <TableRow
                    key={post.id || post.shortcode || post.video_id || index}
                  >
                    <TableCell align="center">
                      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {(() => {
                          const thumbKey = post.id || post.shortcode || post.video_id || index;
                          const thumbUrl =
                            post.yt_thumnail_url || // YouTube API has typo: thumnail instead of thumbnail
                            post.thumbnail_url ||
                            post.image_url ||
                            post.media_url;
                          const openPost = () => {
                            if (platform === 'instagram') {
                              window.open(`https://www.instagram.com/p/${post.shortcode}`, '_blank');
                            } else if (platform === 'youtube') {
                              window.open(`https://www.youtube.com/watch?v=${post.video_id || post.id}`, '_blank');
                            }
                          };
                          // Show the real thumbnail when we have a URL that hasn't failed;
                          // otherwise show the placeholder graphic. Never both.
                          if (thumbUrl && !failedThumbs[thumbKey]) {
                            return (
                              <img
                                onClick={openPost}
                                src={thumbUrl}
                                alt="Post thumbnail"
                                className={classes.thumbnail}
                                style={{ cursor: 'pointer' }}
                                onError={() => setFailedThumbs((prev) => ({ ...prev, [thumbKey]: true }))}
                              />
                            );
                          }
                          return (
                            <Box
                              className={classes.thumbnail}
                              onClick={openPost}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f3f4f6',
                                cursor: 'pointer',
                              }}
                            >
                              <InsertPhotoOutlined style={{ fontSize: 22, color: '#c4c8ce' }} />
                            </Box>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
                        <Box
                          style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                          onClick={() => {
                            if (platform === 'instagram') {
                              window.open(`https://www.instagram.com/${post.creator_details?.id}`, '_blank');
                            } else if (platform === 'youtube') {
                              window.open(`https://www.youtube.com/channel/${post.created_by}`, '_blank');
                            } 
                          }}
                        >
                          <Avatar
                            src={addCacheBuster(
                              post.creator_details?.image_url ||
                              post.profile_picture ||
                              post.thumbnail_url ||
                              post.avatar
                            )}
                            style={{ width: 40, height: 40 }}
                          >
                            {(
                              (post.creator_details?.name ||
                                post.creator_name ||
                                post.creator ||
                                post.handle ||
                                post.channel_name ||
                                '')
                                .substring(0, 2)
                                .toUpperCase()
                            )}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 500, textAlign: 'center' }}>
                              {post.creator_details?.name ||
                                (post.dump && post.dump.snippet && post.dump.snippet.channelTitle) ||
                                post.creator_name ||
                                post.creator ||
                                post.handle ||
                                post.channel_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" style={{ textAlign: 'center' }}>
                              {post.creator_details?.id ||
                                post.creator ||
                                post.handle ||
                                post.channel_id}
                              {/* {post.creator_details?.category && (
                                <span> • {post.creator_details.category}</span>
                              )} */}
                            </Typography>
                          </Box>
                        </Box>
                        {isStaff && platform === 'instagram' && (
                          <Box style={{ display: 'flex', alignItems: 'center' }}>
                            {updatingProfiles[post.creator_details?.id || post.creator_details?.handle || post.handle || post.channel_id] ? (
                              <CircularProgress size={20} />
                            ) : (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateProfile(post);
                                }}
                                size="small"
                                title="Update Profile"
                                style={{ color: '#1E9CB2', padding: 4 }}
                              >
                                <Refresh fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" style={{ textAlign: 'center' }}>
                        {getInfluencerType(post.creator_details?.followers)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                    <Chip
                        label={cleanCategoryName(post.creator_details?.category || post.category)}
                        size="small"
                            style={{
                              backgroundColor: '#e5e7eb',
                              color: '#374151',
                              fontWeight: 500,
                              textAlign: 'center',
                            }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box style={{ display: 'flex', justifyContent: 'center' }}>
                        <Chip
                          label={post.brand}
                          size="small"
                          style={{
                            backgroundColor:
                              getBrandColor(
                                post.brand,
                                competitorBrands.findIndex(
                                  (b) => b.title === post.brand
                                )
                              ) + '20',
                            color: getBrandColor(
                              post.brand,
                              competitorBrands.findIndex(
                                (b) => b.title === post.brand
                              )
                            ),
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" style={{ textAlign: 'center' }}>
                        {post.platform ||
                          (post.post_type ? 'Instagram' : 'YouTube')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box style={{ display: 'flex', justifyContent: 'center' }}>
                        <Chip
                          label={post.post_type || post.media_type || 'Post'}
                          size="small"
                          style={{
                            backgroundColor: '#e5e7eb',
                            color: '#374151',
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        style={{
                          color: engagementRate > 5 ? '#10b981' : '#374151',
                          fontWeight: engagementRate > 5 ? 600 : 400,
                          textAlign: 'center',
                        }}
                      >
                        {formatPercentage(engagementRate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" style={{ fontWeight: 500, textAlign: 'center' }}>
                        {formatNumber(post.view_count || post.reach || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" style={{ textAlign: 'center' }}>
                        {formatNumber(post.likes_count || post.like_count || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" style={{ textAlign: 'center' }}>
                        {formatNumber(
                          post.comments_count || post.comment_count || 0
                        )}
                      </Typography>
                    </TableCell>
                    {/* <TableCell align="center">
                      <Typography variant="body2" style={{ textAlign: 'center' }}>
                        {formatNumber(post.shares_count || post.share_count || 0)}
                      </Typography>
                    </TableCell> */}
                    <TableCell align="center">
                      <Typography variant="body2" style={{ textAlign: 'center' }}>
                        {postDate ? formatDate(postDate) : 'N/A'}
                      </Typography>
                    </TableCell>
                    {isStaff && platform === 'instagram' && (
                      <TableCell align="center">
                        {updatingPosts[post.id || post.shortcode || post.video_id] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton
                            onClick={() => handleUpdatePost(post)}
                            size="small"
                            title="Update Post"
                            style={{ color: '#1E9CB2' }}
                          >
                            <Refresh />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
          </TableContainer>
          {posts.length > 0 && (
            <Box className={classes.paginationContainer}>
              <Box className={classes.paginationInfo}>
                <Typography variant="body2" color="textSecondary">
                  {hasActiveFilters && filteredPosts.length !== posts.length
                    ? `Showing ${filteredPosts.length} of ${posts.length} posts`
                    : `Total: ${posts.length} posts`}
                </Typography>
              </Box>
              <Box className={classes.paginationInfo}>
                <TablePagination
                  component="div"
                  count={filteredPosts.length}
                  page={page}
                  onChangePage={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onChangeRowsPerPage={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading || loadingMetrics}
                  size="small"
                  title="Refresh"
                  style={{ marginLeft: 8 }}
                >
                  <Refresh />
                </IconButton>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

